import axios from '../../axios';
import { useQuery } from '@tanstack/react-query';
import type { TQuizBase, TError } from '@onesource/schemas';
import { ENTITY } from '../entity';

/**
 * Quiz Option
 */
export interface TQuizOption {
  id: string;
  questionId: string;
  optionText: string;
  isCorrect: boolean;
  position: number;
}

/**
 * Quiz Question with options
 */
export interface TQuizQuestion {
  id: string;
  quizId: string;
  questionType: 'multiple_choice' | 'multiple_select' | 'true_false' | 'short_answer' | 'ordering';
  questionText: string;
  explanation: string | null;
  points: number;
  position: number;
  metadata: Record<string, unknown>;
  options: TQuizOption[];
}

/**
 * Quiz with questions and options
 */
export interface TQuizWithQuestions extends TQuizBase {
  questions: TQuizQuestion[];
}

/**
 * Query key for fetching a specific quiz by ID (non-hook version)
 */
export const getQuizQueryKey = (id: string) => [ENTITY, 'quiz', id];

/**
 * Query key for fetching a specific quiz by ID
 * @deprecated Use getQuizQueryKey instead in non-hook contexts
 */
export const useGetQuizQueryKey = (id: string) => getQuizQueryKey(id);

/**
 * Hook to fetch a specific quiz by ID with its questions and options
 *
 * @param id - Quiz ID (UUID)
 */
export const useGetQuiz = (id: string) => {
  return useQuery<TQuizWithQuestions, TError>({
    queryKey: useGetQuizQueryKey(id),
    queryFn: async () => {
      const res = await axios.get<TQuizWithQuestions>(`/community-hub/quizzes/${id}`);
      return res.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
